import axios from "axios";
import {
  EventsResponse,
  EventDetail,
  TerritoryOption,
  EventFilter,
  HistoricalContextResponse,
  AttendanceStatus,
  TeamAttendanceMap,
} from "./types";
import { auth } from "./firebase";

const API_BASE = process.env.REACT_APP_API_URL ?? "";

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const idToken = await user.getIdToken();
    config.headers.Authorization = `Bearer ${idToken}`;
  }
  return config;
});

function buildEventParams(filter?: Partial<EventFilter>): Record<string, string> {
  const params: Record<string, string> = {};
  if (!filter) return params;

  if (filter.level && filter.code) {
    params.level = filter.level;
    params.code = filter.code;
  }
  if (filter.categories && filter.categories.length > 0) {
    params.categories = filter.categories.join(",");
  }
  if (filter.minScore !== undefined && filter.minScore > 1) {
    params.min_score = String(filter.minScore);
  }
  if (filter.maxScore !== undefined && filter.maxScore < 10) {
    params.max_score = String(filter.maxScore);
  }
  if (filter.hideDefaults === false) {
    params.hide_defaults = "false";
  }
  return params;
}

export async function fetchEvents(filter?: Partial<EventFilter>): Promise<EventsResponse> {
  const params = buildEventParams(filter);
  const { data } = await api.get<EventsResponse>("/events/", { params });
  return data;
}

export async function fetchEventDetail(id: number): Promise<EventDetail> {
  const { data } = await api.get<EventDetail>(`/events/${id}`);
  return data;
}

export async function generateEventAI(id: number): Promise<EventDetail> {
  const { data } = await api.get<EventDetail>(`/events/${id}?generate=true`);
  return data;
}

export async function fetchHistoricalContext(id: number): Promise<HistoricalContextResponse> {
  const { data } = await api.get<HistoricalContextResponse>(`/events/${id}/historical`);
  return data;
}

export async function fetchTerritories(level: string): Promise<TerritoryOption[]> {
  const { data } = await api.get<TerritoryOption[]>(`/events/territories/${level}`);
  return data;
}

export function getCalendarUrl(id: number): string {
  return `${API_BASE}/events/${id}/calendar`;
}

export function getCheatsheetHtmlUrl(id: number): string {
  return `${API_BASE}/events/${id}/cheatsheet?format=html`;
}

export async function downloadCheatsheetPdf(id: number): Promise<Blob> {
  const { data } = await api.get<Blob>(`/events/${id}/cheatsheet?format=pdf`, {
    responseType: "blob",
  });
  return data;
}

export async function fetchAttendance(eventId: number): Promise<AttendanceStatus> {
  const { data } = await api.get<{ status: AttendanceStatus }>(`/attendance/${eventId}`);
  return data.status;
}

export async function postAttendance(eventId: number, status: AttendanceStatus): Promise<void> {
  await api.post(`/attendance/${eventId}`, { status });
}

export async function fetchTeamAttendance(obvodCode: string): Promise<TeamAttendanceMap> {
  const { data } = await api.get<TeamAttendanceMap>(`/team/${obvodCode}/attendance`);
  return data;
}

export async function addManualEvent(event: {
  title: string;
  date_start: string;
  city: string;
  description?: string;
  organizer?: string;
  contact?: string;
}): Promise<EventDetail> {
  const { data } = await api.post<EventDetail>("/events/", event);
  return data;
}

// ─── Phoenix Electoral Analytics — strategy context ──────────────────────────

export interface PhoenixContextSummary {
  sections_found: string[];
  file_count: number;
  uploaded_at: string;
}

export interface PhoenixContext {
  sections: Record<string, string>;
  uploaded_at: string;
  file_count: number;
}

export async function uploadStrategyFiles(
  files: File[],
  onProgress?: (pct: number) => void,
): Promise<PhoenixContextSummary> {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  const response = await api.post<PhoenixContextSummary>(
    "/api/users/me/strategy/upload",
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    },
  );
  return response.data;
}

export async function getStrategy(): Promise<PhoenixContext | null> {
  const response = await api.get<PhoenixContext | null>("/api/users/me/strategy");
  return response.data;
}

export async function deleteStrategy(): Promise<void> {
  await api.delete("/api/users/me/strategy");
}
